import { applyNormalize, parseUrlInput } from "../normalizeInput";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const DEFAULT_POSTHOG_HOST = "https://app.posthog.com";

function postHogBase(host?: string): string {
  return parseUrlInput(host ?? "")
    ? applyNormalize(host ?? "", (url) => url)
    : DEFAULT_POSTHOG_HOST;
}

export const postHog = defineConnector({
  identifier: "postHog",
  name: "PostHog",
  tagLine: "Product analytics platform",
  keywords:
    "analytics events funnels session replay feature flags experiments cohorts insights",
  logo,
  protocol: "http",
  tags: ["data", "engineering"],
  defaultUrlPattern: ({ host }) => exactUrlToRegex(postHogBase(host)),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Get a personal API key",
      items: [
        {
          kind: "markdown",
          content:
            "Sign in to [PostHog](https://us.posthog.com/) and navigate to **Settings** > **Personal API Keys** to create a new key. EU Cloud users should sign in at [eu.posthog.com](https://eu.posthog.com/) instead.",
        },
      ],
    },
    {
      title: "Enter your API key",
      items: [
        {
          kind: "input",
          label: "PostHog API key",
          required: true,
          placeholder: "phx_...",
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
    {
      title: "Enter your PostHog host",
      items: [
        {
          kind: "markdown",
          content:
            "If you’re using PostHog Cloud US, leave this as `https://app.posthog.com` (or use `https://us.posthog.com`). For EU, use `https://eu.posthog.com`. For self-hosted, enter your instance URL.",
        },
        {
          kind: "url",
          label: "PostHog host",
          required: true,
          defaultValue: DEFAULT_POSTHOG_HOST,
          placeholder: "https://us.posthog.com",
          target: { kind: "configuration", field: "host" },
          helperText: "Your PostHog instance URL.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ host }) => `${postHogBase(host)}/api/projects`,
  },
  publicEnvVars: ({ host }) => ({
    POST_HOG_URL: postHogBase(host),
  }),
});
